import { createAsyncThunk } from '@reduxjs/toolkit';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import {
  validateBillingAddress,
  validateShippingAddress,
  validateEmail,
  goToShippingAddress,
  goToBillingAddress,
  goToShippingMethods,
  setBillingAddressToShippingAddress,
  setIsValidated,
  setAlreadySignedUpForNewsletter,
} from '../Slices/ShippingSlice';
import { addMessage, clearMessages } from '../Slices/MessageSlice.jsx';
import { addMessage as addShippingMessage } from '../Slices/ShippingSlice.jsx';
import { clearMessages as clearShippingMessages } from '../Slices/ShippingSlice.jsx';
import { handlePfasProducts } from '../Helpers/GeneralHelper.js';
import { isLoggedIn } from '../Helpers/UiHelper.js';
import CommerceApiFetchWrapper from '/Foundation/Common/code/Utils/CommerceApiFetchWrapper.js';
import { updateShippingMethod, updateExternalShippingMethod } from '/Foundation/Common/code/Helpers/ShippingHelper.js';
import { trackCheckoutClick } from '../Analytics/Adobe.js';
import { br_checkout } from '/Foundation/Bloomreach/code/Events/BloomreachCheckout.js';
import { br_identify } from '/Foundation/Bloomreach/code/Events/Bloomreach.js';
import { br_consentOptIn } from '/Foundation/Bloomreach/code/Events/BloomreachConsent.js';
import { br_getCustomerConsents } from '/Foundation/Bloomreach/code/Events/BloomreachConsent.js';
import { setCartIdCookie } from '/Foundation/React/code/Utilities/HelperUtilities';
import Cookies from 'js-cookie';
import { saveNshiftSelection } from '../Helpers/PaymentHelper.js';
import { sendAbTestingHit } from '/Foundation/ABTesting/code/Views/Foundation/ABTesting/abTestingClient.js';
import { setEmailCookie } from '/Foundation/Common/code/TrackingCookieApi';
import { AdobeAnalyticsUtil } from '/Foundation/Common/code/Views/Foundation/Common/AdobeAnalytics/AdobeAnalyticsUtil.js';
import { SIGNUP_SOURCE } from '/Feature/AdobeAnalytics/code/Views/Feature/AdobeAnalytics/Constants/SignupSource.js';

const STORE_LOGO_URL = '/img/thule.png';

/**
 * Checks newsletter consent for a customer if wantNewsletter is false.
 * @param {boolean} wantNewsletter - Whether the user wants the newsletter
 * @param {string} email - Customer email address
 * @returns {Promise<boolean>} - Returns true if user is signed up for newsletter, false otherwise
 */
const checkNewsletterConsent = async (wantNewsletter, email) => {
  let signedUpForNewsletter = wantNewsletter;
  if (!signedUpForNewsletter && email) {
    try {
      const hasConsent = await br_getCustomerConsents(email);
      if (hasConsent) {
        signedUpForNewsletter = true;
      }
    } catch (error) {
      // If consent check fails, fall back to wantNewsletter value
      console.error('Error checking newsletter consent, using wantNewsletter value:', error);
    }
  }
  return signedUpForNewsletter;
};

export const continueFromContact = () => {
  return (dispatch, getState) => {
    dispatch(validateEmail(getState().Shipping.email.value));
    const state = getState();
    if (state.Shipping.email.hasError || !state.Shipping.email.value) return;

    sendAbTestingHit('ContactPage_To_ShippingAddress_Continue_Clicked');
    setEmailCookie(state.Shipping.email.value);
    dispatch(subscribeToNewsletter());
    dispatch(setContact());
  };
};

export const setContact = createAsyncThunk('setContact', async (_, { getState, dispatch, rejectWithValue }) => {
  const state = getState();
  const email = state.Shipping.email.value;

  const signedUpForNewsletter = await checkNewsletterConsent(state.Shipping.wantNewsletter, email);
  dispatch(setAlreadySignedUpForNewsletter(signedUpForNewsletter));

  const result = await CommerceApiFetchWrapper('/cart/contact', {
    method: 'POST',
    body: JSON.stringify({
      Email: email,
      SignedUpForNewsletter: signedUpForNewsletter,
    }),
  });

  // The address step re-sends the flag, so a failure here should not block the customer.
  dispatch(goToShippingAddress());

  if (!result) return rejectWithValue('setContact failed');
  return result;
});

export const showBillingFields = createAsyncThunk(
  'showBillingFields',
  async (_, { getState, dispatch, fulfillWithValue, rejectWithValue }) => {
    dispatch(clearMessages());
    dispatch(clearShippingMessages());
    dispatch(validateShippingAddress());

    let state = getState();
    let entries = Object.entries(state.Shipping.shipping);
    let hasErrors = entries.some((x) => x.some((y) => y.hasError)) || state.Shipping.email.hasError;
    if (hasErrors || state.Shipping.isRestrictedPostalCode) return;

    if (!state.Shipping.reintroduceContactPage && state.Shipping.email.value)
      setEmailCookie(state.Shipping.email.value);
    sendAbTestingHit('ShippingAddress_To_ShippingMethod_Continue_Clicked');

    br_checkout(state.Cart);

    let cartResult;
    let country = state.Cart.country;
    let shippingFields = state.Shipping.shipping;
    let stateValue = shippingFields.state.value ? shippingFields.state.value : 'NA';

    let activePfasMarkets = window._THULEDATA._Checkout.activePfasMarkets;
    let activePfasStates = window._THULEDATA._Checkout.activePfasStates;

    const shouldHandlePfasProducts = activePfasMarkets.includes(country) && activePfasStates.includes(stateValue);
    if (shouldHandlePfasProducts) cartResult = await dispatch(handlePfasProducts());

    if (shouldHandlePfasProducts && !cartResult.payload.canCheckout) {
      return rejectWithValue('showBillingFields failed');
    }

    const shouldValidateShipping = ['US', 'CA'].includes(state.Cart.country);
    if (shouldValidateShipping && !state.Shipping.hasDoneAddressLookup) {
      const result = await dispatch(lookupShippingAddress());
      state = getState();
      if (!result.error && !state.Shipping.addressSuggestions) {
        dispatch(trackCheckoutClick());
        dispatch(continueFromShippingAddress());
      }
    } else {
      dispatch(trackCheckoutClick());
      dispatch(continueFromShippingAddress());
    }

    return fulfillWithValue(true);
  }
);

export const continueFromShippingAddress = () => {
  return (dispatch, getState) => {
    let state = getState();
    // In the variation the newsletter opt-in already fired on the contact page.
    if (!state.Shipping.reintroduceContactPage) dispatch(subscribeToNewsletter());
    if (state.Shipping.useSameAddressForBilling) {
      dispatch(setBillingAddressToShippingAddress());
      dispatch(setAddresses());
    } else {
      dispatch(setShippingAddress());
    }
  };
};

export const subscribeToNewsletter = () => {
  return (dispatch, getState) => {
    let state = getState();
    if (state.Shipping.wantNewsletter) {
      br_consentOptIn(state.Shipping.email.value);
      AdobeAnalyticsUtil.DataLayer.TrackEvent('newsletter_signup', {
        newsletter: { variant: SIGNUP_SOURCE.CHECKOUT },
      });
    }
  };
};

export const continueFromBillingAddress = () => {
  return (dispatch, getState) => {
    dispatch(clearMessages());
    dispatch(validateBillingAddress());
    let state = getState();

    let entries = Object.entries(state.Shipping.billing);

    let hasErrors = entries.some((x) => x.some((y) => y.hasError));

    if (!hasErrors) {
      dispatch(trackCheckoutClick());
      dispatch(setBillingAddress());
    }
  };
};

export const lookupShippingAddress = createAsyncThunk(
  'lookupShippingAddress',
  async (_, { getState, dispatch, rejectWithValue }) => {
    let state = getState();
    let shippingAddress = state.Shipping.shipping;
    let body = JSON.stringify({
      StreetName: shippingAddress.address.value,
      AdditionalStreetInfo: shippingAddress.address2.value ? shippingAddress.address2.value : 'NA',
      City: shippingAddress.city.value,
      PostalCode: shippingAddress.zipCode.value,
      State: shippingAddress.state.value ? shippingAddress.state.value : 'NA',
      Country: state.Cart.country,
    });

    let result = await CommerceApiFetchWrapper('/addressvalidation', {
      method: 'POST',
      body: body,
    });

    if (!result.success || !result.validatedAddressList) {
      window.scrollTo(0, 0);

      dispatch(
        addShippingMessage({
          id: new Date().valueOf() * Math.random(),
          content: state.Translations.shipping.addressValidationErrorMessage,
          type: 'error',
        })
      );
      return rejectWithValue('lookupShippingAddress failed');
    }

    return result;
  }
);

export const setShippingAddress = createAsyncThunk(
  'setShippingAddress',
  async (_, { getState, dispatch, rejectWithValue }) => {
    let state = getState();
    let email = state.Shipping.email;
    let shippingFields = state.Shipping.shipping;
    let country = state.Cart.country;

    const signedUpForNewsletter = await checkNewsletterConsent(state.Shipping.wantNewsletter, email.value);
    dispatch(setAlreadySignedUpForNewsletter(signedUpForNewsletter));

    const body = JSON.stringify({
      FirstName: shippingFields.firstName.value,
      LastName: shippingFields.lastName.value,
      StreetName: shippingFields.address.value,
      AdditionalStreetInfo: shippingFields.address2.value,
      City: shippingFields.city.value,
      State: shippingFields.state.value ? shippingFields.state.value : 'NA',
      PostalCode: shippingFields.zipCode.value,
      Country: country,
      Email: email.value,
      Phone: parsePhoneNumberFromString(shippingFields.phone.value, shippingFields.phoneCountryCode.value).number,
      SignedUpForNewsletter: signedUpForNewsletter,
    });

    br_identify(email.value);
    let result = await CommerceApiFetchWrapper('/cart/shipping-address', {
      method: 'POST',
      body: body,
    });

    if (!result || !result.shippingAddress?.email || !result.shippingAddress?.streetName) {
      window.scrollTo(0, 0);

      dispatch(
        addMessage({
          id: new Date().valueOf() * Math.random(),
          content: window._THULEDATA._Cart.generalErrorMessage,
          type: 'error',
        })
      );
      return rejectWithValue('setShippingAddress failed');
    }

    if (isLoggedIn() && !state.Shipping.hasAddressFromProfile && !state.Cart.hasShippingAddressSet) {
      await CommerceApiFetchWrapper('/customer/shipping-address', {
        method: 'POST',
        body: body,
      });
    }

    dispatch(setIsValidated());
    dispatch(goToBillingAddress());
    dispatch(loadShippingMethods());

    return result;
  }
);

export const setBillingAddress = createAsyncThunk(
  'setBillingAddress',
  async (_, { getState, dispatch, rejectWithValue }) => {
    let state = getState();
    let billingFields = state.Shipping.billing;
    let country = state.Cart.country;
    const body = JSON.stringify({
      FirstName: billingFields.firstName.value,
      LastName: billingFields.lastName.value,
      StreetName: billingFields.address.value,
      AdditionalStreetInfo: billingFields.address2.value,
      City: billingFields.city.value,
      State: billingFields.state.value ? billingFields.state.value : 'NA',
      PostalCode: billingFields.zipCode.value,
      Country: country,
    });

    let result = await CommerceApiFetchWrapper('/cart/billing-address', {
      method: 'POST',
      body: body,
    });

    if (!result) {
      window.scrollTo(0, 0);

      dispatch(
        addMessage({
          id: new Date().valueOf() * Math.random(),
          content: window._THULEDATA._Cart.generalErrorMessage,
          type: 'error',
        })
      );
      return rejectWithValue('setBillingAddress failed');
    }

    dispatch(loadShippingMethods());
    dispatch(goToShippingMethods());

    return result;
  }
);

export const setAddresses = createAsyncThunk('setAddresses', async (_, { getState, dispatch, rejectWithValue }) => {
  let state = getState();
  let email = state.Shipping.email;
  let shippingFields = state.Shipping.shipping;
  let billingFields = state.Shipping.billing;
  let country = state.Cart.country;

  const signedUpForNewsletter = await checkNewsletterConsent(state.Shipping.wantNewsletter, email.value);
  dispatch(setAlreadySignedUpForNewsletter(signedUpForNewsletter));

  const addresses = {
    ShippingAddress: {
      FirstName: shippingFields.firstName.value,
      LastName: shippingFields.lastName.value,
      StreetName: shippingFields.address.value,
      AdditionalStreetInfo: shippingFields.address2.value,
      City: shippingFields.city.value,
      State: shippingFields.state.value ? shippingFields.state.value : 'NA',
      PostalCode: shippingFields.zipCode.value,
      Country: country,
      Email: email.value,
      Phone: parsePhoneNumberFromString(shippingFields.phone.value, shippingFields.phoneCountryCode.value).number,
      SignedUpForNewsletter: signedUpForNewsletter,
    },
    BillingAddress: {
      FirstName: billingFields.firstName.value,
      LastName: billingFields.lastName.value,
      StreetName: billingFields.address.value,
      AdditionalStreetInfo: billingFields.address2.value,
      City: billingFields.city.value,
      State: billingFields.state.value ? shippingFields.state.value : 'NA',
      PostalCode: billingFields.zipCode.value,
      Country: country,
    },
  };

  const body = JSON.stringify(addresses);

  br_identify(email.value);
  let result = await CommerceApiFetchWrapper('/cart/addresses', {
    method: 'POST',
    body: body,
  });

  if (!result || !result.shippingAddress?.email || !result.shippingAddress?.streetName) {
    window.scrollTo(0, 0);

    dispatch(
      addMessage({
        id: new Date().valueOf() * Math.random(),
        content: window._THULEDATA._Cart.generalErrorMessage,
        type: 'error',
      })
    );
    return rejectWithValue('setAddresses failed');
  }

  if (isLoggedIn() && !state.Shipping.hasAddressFromProfile && !state.Cart.hasShippingAddressSet) {
    await CommerceApiFetchWrapper('/customer/shipping-address', {
      method: 'POST',
      body: JSON.stringify(addresses.ShippingAddress),
    });
  }

  dispatch(setIsValidated());
  dispatch(loadShippingMethods());
  dispatch(goToShippingMethods());

  return result;
});

export const getShippingMethods = createAsyncThunk('getShippingMethods', async (_, { dispatch, rejectWithValue }) => {
  let result = await CommerceApiFetchWrapper('/cart/shipping-methods', {
    method: 'GET',
  });

  if (!result?.cart || !result?.shippingMethods?.shippingMethods?.length > 0) {
    dispatch(
      addShippingMessage({
        id: new Date().valueOf() * Math.random(),
        content: window._THULEDATA._Cart.generalErrorMessage,
        type: 'error',
      })
    );
    return rejectWithValue('getShippingMethods failed');
  }

  return result;
});

/**
 * Builds a copy of the nshift-shipping-options response containing only options
 * that match (storeOnly) or exclude (non-store) additionalValues.isStore === "true".
 * Drops subShipments with no options; if none remain, returns null.
 * @param {object} result - Raw API response
 * @param {boolean} storeOnly - true: keep only store pickup options; false: keep all others
 * @returns {object|null}
 */
export function splitNshiftShippingOptionsResponse(result, storeOnly) {
  if (!result?.optionsData?.subShipments?.length) return null;

  const matchesStore = (opt) => opt?.additionalValues?.isStore === 'true';
  const pred = storeOnly ? matchesStore : (opt) => !matchesStore(opt);

  const subShipments = result.optionsData.subShipments
    .map((ss) => ({
      ...ss,
      options: (ss.options || []).filter(pred),
    }))
    .filter((ss) => ss.options.length > 0);

  if (!subShipments.length) return null;

  const keptIds = new Set(subShipments.map((s) => s.subShipmentId));
  const subShipmentToLinesMapping = (result.subShipmentToLinesMapping || []).filter((m) =>
    keptIds.has(m.subShipmentId)
  );

  return {
    cart: result.cart ? structuredClone(result.cart) : result.cart,
    optionsData: {
      ...result.optionsData,
      subShipments,
    },
    subShipmentToLinesMapping,
  };
}

const overrideStoreSubShipmentLogoUrl = (result) => {
  if (!result?.optionsData?.subShipments?.length) return result;

  return {
    ...result,
    optionsData: {
      ...result.optionsData,
      subShipments: result.optionsData.subShipments.map((subShipment) => ({
        ...subShipment,
        options: (subShipment?.options || []).map((option) => {
          if (option?.additionalValues?.isStore !== 'true') return option;

          return {
            ...option,
            logoUrl: STORE_LOGO_URL,
          };
        }),
      })),
    },
  };
};

export const getExternalShippingMethods = createAsyncThunk(
  'getExternalShippingMethods',
  async (_, { dispatch, rejectWithValue, getState }) => {
    const nshift = getState()?.Shipping?.nshiftOptionsData;

    let payload = null;

    if (nshift?.optionsData) {
      payload = JSON.stringify({
        sessionId: nshift.optionsData.sessionId,
      });
    }

    let result = await CommerceApiFetchWrapper('/cart/nshift-shipping-options', {
      method: 'POST',
      body: payload,
    });
    result = overrideStoreSubShipmentLogoUrl(result);

    if (result?.cart?.id) setCartIdCookie(result.cart.id);

    if (!result?.optionsData?.subShipments?.length) {
      dispatch(
        addShippingMessage({
          id: new Date().valueOf() * Math.random(),
          content:
            result?.errorCode === '1'
              ? getState().Translations.shipping.shippingValidationMessages.invalidAddressErrorMessage
              : window._THULEDATA._Cart.generalErrorMessage,
          type: 'error',
        })
      );
      return rejectWithValue('getExternalShippingMethods failed');
    }

    const responseHasStoreOptions = result?.optionsData?.subShipments?.some((subShipment) =>
      (subShipment?.options || []).some((option) => option?.additionalValues?.isStore === 'true')
    );
    const responseHasNonStoreOptions = result?.optionsData?.subShipments?.some((subShipment) =>
      (subShipment?.options || []).some((option) => option?.additionalValues?.isStore !== 'true')
    );
    const uniqueSubShipmentIdsCount = new Set(
      (result?.optionsData?.subShipments || []).map((subShipment) => subShipment?.subShipmentId).filter(Boolean)
    ).size;
    const shouldShowDeliveryStoreTabs =
      responseHasStoreOptions && responseHasNonStoreOptions && uniqueSubShipmentIdsCount > 1;

    // Keep the full response as-is unless the UI needs separate store/non-store tabs.
    if (!shouldShowDeliveryStoreTabs) {
      return {
        result,
        storeOptions: null,
        nonStoreOptions: null,
      };
    }

    // First response can include both delivery + store pickup options. For the non-store flow we must
    // remove store options, then fetch store-only options using `getStoreOnly`.
    const nonStoreOptions = splitNshiftShippingOptionsResponse(result, false);

    const sessionId = result?.optionsData?.sessionId ?? nshift?.optionsData?.sessionId;
    const storePayload = JSON.stringify({
      sessionId,
      getStoreOnly: true,
    });

    let storeResult = await CommerceApiFetchWrapper('/cart/nshift-shipping-options', {
      method: 'POST',
      body: storePayload,
    });
    storeResult = overrideStoreSubShipmentLogoUrl(storeResult);

    if (storeResult?.cart?.id) setCartIdCookie(storeResult.cart.id);

    const storeOptions = splitNshiftShippingOptionsResponse(storeResult, true);

    return {
      result: null,
      nonStoreOptions,
      storeOptions,
    };
  }
);

export const loadShippingMethods = () => (dispatch, getState) => {
  const enableNshift = Boolean(getState()?.Shipping?.nshiftEnabled);
  return dispatch(enableNshift ? getExternalShippingMethods() : getShippingMethods());
};

export const setShippingMethod = createAsyncThunk('setShippingMethod', async (_, { dispatch }) => {
  dispatch(clearShippingMessages());
  var cart = await dispatch(updateShippingMethod());
  var result = cart.payload;

  return result;
});

export const setExternalShippingMethod = createAsyncThunk(
  'setExternalShippingMethod',
  async (_, { dispatch, getState }) => {
    dispatch(clearShippingMessages());
    var cart = await dispatch(updateExternalShippingMethod());
    var result = cart.payload;

    if (!cart.error) saveNshiftSelection(Cookies.get('cartId'), getState().Shipping.selectedExternalShippingMethod);

    return result;
  }
);
